import { useDisclosure } from '@mantine/hooks';
import {
  Modal,
  UnstyledButton,
  Table,
  ScrollArea,
  TextInput,
  Textarea,
  Button,
  Tooltip,
  Alert,
  rem,
} from '@mantine/core';
import { DateInput} from '@mantine/dates';
import { dateFormat } from '@/index';
import { TbTrashXFilled } from 'react-icons/tb';
import SectionErrorNotification from '@/components/Section/SectionErrorNotification';
import SectionLoadingOverlay from '@/components/Section/SectionLoadingOverlay';
import { HiPencil } from 'react-icons/hi2';
import useExceptionalEvents from '@/hooks/employee/useExceptionalEvents';
import useDeleteExceptionalEvent from '@/hooks/employee/useDeleteExceptionalEvent';
import useCreateExceptionalEvent from '@/hooks/employee/useCreateExceptionalEvent';
import {
  createExceptionalEventSchema,
  type CreateExceptionalEvent,
} from '@/schemas/API/POST/employee/exceptional-event/request';
import { useForm, zodResolver } from '@mantine/form';
import dayjs from 'dayjs';
import { IconInfoCircle, IconClock } from '@tabler/icons-react';
import { useState, type ChangeEvent } from 'react';
import { displayError } from '@/utils/error/errorHandler';
import useEmployeeSchedule from '@/hooks/employee/useEmployeeSchedule';
import TimeInput from '@/components/Input/TimeInput/TimeInput';
import { useAvailabilityRefresh } from '../../EmployeeAvailabilitySection';

export default function ExceptionalEventsCard() {
  const { exceptionalEvents, error } = useExceptionalEvents();
  const { deleteExceptionalEvent } = useDeleteExceptionalEvent();
  const { createExceptionalEvent } = useCreateExceptionalEvent();
  const { mutate } = useEmployeeSchedule();
  const { refresh } = useAvailabilityRefresh();

  const createForm = useForm<CreateExceptionalEvent>({
    initialValues: {
      date: dayjs().format(dateFormat),
      name: '',
      note: '',
      startTime: '',
      endTime: '',
    },
    validate: zodResolver(createExceptionalEventSchema),
  });

  const [isCreateError, setIsCreateError] = useState<boolean>(false);
  const [opened, { open, close }] = useDisclosure(false);

  if (error) return <SectionErrorNotification />;
  if (!exceptionalEvents) return <SectionLoadingOverlay />;

  async function handleDelete(id: number) {
    await deleteExceptionalEvent(id);
    await mutate();
    refresh();
  }

  async function handleCreate() {
    const valRes = createForm.validate();
    if (valRes.hasErrors) {
      console.error('Invalid create form values:', valRes.errors);
      return;
    }

    try {
      await createExceptionalEvent(createForm.values);
    } catch (err) {
      displayError(err);
      setIsCreateError(true);
      return;
    }

    createForm.reset();
    setIsCreateError(false);

    await mutate();
    refresh();
  }

  return (
    <>
      <Modal
        size="xl"
        opened={opened}
        onClose={close}
        centered
        title="Výjimečné údalosti"
      >
        <div className="flex flex-row gap-3 mb-5">
          <div className="w-1/2 flex flex-col">
            <TextInput
              variant="filled"
              radius="md"
              placeholder="Navštěva lékaře"
              label="Název údalosti"
              {...createForm.getInputProps('name')}
            />
            <DateInput
              variant="filled"
              radius="md"
              label="Den"
              placeholder="note"
              valueFormat={dateFormat}
              value={dayjs(createForm.values.date, dateFormat).toDate()}
              onChange={date => {
                createForm.setFieldValue(
                  'date',
                  dayjs(date).format(dateFormat)
                );
              }}
              error={createForm.getInputProps('date').error}
              locale="cs"
            />

              <TimeInput
                  description="Od"
                  leftSection={
                    <IconClock
                        style={{ width: rem(16), height: rem(16) }}
                        stroke={1.5}
                    />
                  }
                  value={createForm.values.startTime}
                  onChange={(startTime) => {
                    createForm.setFieldValue('startTime', startTime);
                  }}
              />
            <TimeInput
              description="Do"
              value={createForm.values.endTime}
              leftSection={
                <IconClock
                  style={{ width: rem(16), height: rem(16) }}
                  stroke={1.5}
                />
              }
              onChange={(endTime) => {
                createForm.setFieldValue('endTime', endTime);
              }}
            />
          </div>
          <div className="w-1/2 flex flex-col justify-between">
            <Textarea
              variant="filled"
              radius="md"
              placeholder="Budu u lékaře..."
              label="Poznámka"
              autosize
              minRows={3}
              maxRows={7}
              {...createForm.getInputProps('note')}
            />
            <Button
              variant="filled"
              color="indigo"
              radius="md"
              onClick={handleCreate}
            >
              Vytvořit
            </Button>
          </div>
        </div>

        {isCreateError && (
          <Alert
            variant="light"
            color="red"
            radius="xl"
            title="Chyba!"
            icon={<IconInfoCircle />}
            className="mb-3"
          >
            Nekorektní údaje, zkontrolujte kolize.
          </Alert>
        )}

        {exceptionalEvents.content.length > 0 ? (
          <ScrollArea h={400} offsetScrollbars>
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Název</Table.Th>
                  <Table.Th>Datum</Table.Th>
                  <Table.Th>Od</Table.Th>
                  <Table.Th>Do</Table.Th>
                  <Table.Th></Table.Th>
                </Table.Tr>
              </Table.Thead>

              <Table.Tbody>
                {exceptionalEvents.content
                  .sort((a, b): number => {
                    if (a.date < b.date) return -1;
                    else if (a.date > b.date) return 1;
                    else {
                      if (a.startTime < b.startTime) return -1;
                      else if (a.startTime > b.startTime) return 1;
                      else return 0;
                    }
                  })
                  .map(a => (
                    <Table.Tr key={a.id}>
                      <Table.Td>
                        <Tooltip label={a.note}>
                          <UnstyledButton>{a.name}</UnstyledButton>
                        </Tooltip>
                      </Table.Td>
                      <Table.Td>{a.date}</Table.Td>
                      <Table.Td>{a.startTime}</Table.Td>
                      <Table.Td>{a.endTime}</Table.Td>
                      <Table.Td>
                        <UnstyledButton
                          onClick={() => {
                            handleDelete(a.id);
                          }}
                        >
                          <TbTrashXFilled size="1.5rem" />
                        </UnstyledButton>
                      </Table.Td>
                    </Table.Tr>
                  ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        ) : (
          <div className="font-semibold text-center">
            Nejsou žádné výjimečné události
          </div>
        )}
      </Modal>

      <UnstyledButton onClick={open}>
        <div className="flex flex-row items-center justify-between p-2.5 border rounded-lg border-card">
          <h1 className="font-semibold">Výjimečné události</h1>
          <HiPencil size="1.5rem" className="shrink-0" />
        </div>
      </UnstyledButton>
    </>
  );
}
